import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@hooks/useAuth";
import { useNotificationStore } from "@stores/notifications/notificationStore";

const UNREAD_POLL_INTERVAL_MS = 15_000;

export const NotificationStreamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const startSse = useNotificationStore((s) => s.startSse);
    const stopSse = useNotificationStore((s) => s.stopSse);
    const startPusher = useNotificationStore((s) => s.startPusher);
    const stopPusher = useNotificationStore((s) => s.stopPusher);
    const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
    const subscribeRealtime = useNotificationStore((s) => s.subscribeRealtime);

    useEffect(() => {
        const unsubscribe = subscribeRealtime(() => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        });
        return unsubscribe;
    }, [queryClient, subscribeRealtime]);

    useEffect(() => {
        if (!isAuthenticated) {
            stopSse();
            stopPusher();
            return;
        }

        void fetchUnreadCount();
        startSse();
        startPusher();

        let pollId: number | undefined;

        const syncUnread = () => {
            void useNotificationStore.getState().fetchUnreadCount();
        };

        const startPoll = () => {
            if (pollId !== undefined) return;
            pollId = window.setInterval(syncUnread, UNREAD_POLL_INTERVAL_MS);
        };

        const stopPoll = () => {
            if (pollId !== undefined) {
                clearInterval(pollId);
                pollId = undefined;
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                syncUnread();
                startPoll();
            } else {
                stopPoll();
            }
        };

        if (document.visibilityState === "visible") {
            startPoll();
        }
        document.addEventListener("visibilitychange", onVisibilityChange);

        const onOnline = () => syncUnread();
        window.addEventListener("online", onOnline);

        // Leaving the page must free its live connection: browsers hold only ~6 per host, and a
        // stream kept by a page in the back/forward cache would queue every later request.
        const onPageHide = () => stopSse();
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) startSse();
        };
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);

        return () => {
            stopPoll();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("online", onOnline);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
            stopSse();
            stopPusher();
        };
    }, [isAuthenticated, startSse, stopSse, startPusher, stopPusher, fetchUnreadCount]);

    return <>{children}</>;
};
